import { memo, useEffect, useState, type JSX } from 'react'

import { AlarmOutlined, TodayOutlined } from '@mui/icons-material'
import { Tooltip } from '@mui/material'
import { SmepEventHealthEnum, SmepEventStateEnum } from '@nativewaves/nw-cloud-sdk/smep'
import { format } from 'date-fns'
import { upperFirst } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { DATE_TIME_FORMAT } from '@shared/utils/support'
import { StateVisualizer } from 'domains/Workspaces/Events/layouts'
import { isClearedTime } from 'domains/Workspaces/Events/schedule'
import { getMinutesToStart, getUpcomingProximity } from 'domains/Workspaces/Events/upcomingProximity'

export type StatusCellProps = {
  state: SmepEventStateEnum
  health: SmepEventHealthEnum
  startTime: string
}

/** The countdown reads in whole minutes, so redrawing twice a minute is resolution enough. */
const COUNTDOWN_TICK = 30_000

/**
 * The state chip. An upcoming event starting today — or within the hour — trades
 * the muted `Upcoming` label for its countdown, so it stands out from the ones
 * that are still days away.
 *
 * The countdown runs on its own clock rather than on the status batch poll: this cell
 * is memoized, and a waiting event's row data does not change between polls, so a
 * re-render of the list around it never reaches this component.
 */
const StatusCellRaw = ({ state, health, startTime }: StatusCellProps): JSX.Element => {
  const { t } = useTranslation('domain')

  // Only a scheduled upcoming event has anything that moves - every other chip is a static label.
  const isCountingDown = state === SmepEventStateEnum.Upcoming && !isClearedTime(startTime)

  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    if (!isCountingDown) {
      return
    }

    const interval = setInterval(() => setNow(new Date()), COUNTDOWN_TICK)

    return () => clearInterval(interval)
  }, [isCountingDown])

  const proximity = getUpcomingProximity(state, startTime, now)

  if (!proximity) {
    return (
      <StateVisualizer variant="text-sm" state={state} health={health}>
        {upperFirst(state.toLowerCase())}
      </StateVisualizer>
    )
  }

  const minutesToStart = getMinutesToStart(startTime, now)

  return (
    <Tooltip title={t('Workspace.Event.Upcoming.tooltip', { date: format(new Date(startTime), DATE_TIME_FORMAT) })}>
      <StateVisualizer variant="text-sm" state={state} health={health} proximity={proximity}>
        {proximity === 'imminent' ? <AlarmOutlined fontSize="inherit" /> : <TodayOutlined fontSize="inherit" />}
        {proximity === 'today'
          ? t('Workspace.Event.Upcoming.today')
          : minutesToStart === 0
            ? t('Workspace.Event.Upcoming.dueNow')
            : t('Workspace.Event.Upcoming.inMinutes', { count: minutesToStart })}
      </StateVisualizer>
    </Tooltip>
  )
}

export const StatusCell = memo(StatusCellRaw)
